import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useDispatch } from 'react-redux'

// Actions
import { actions as artistsActions } from 'modules/artists/actions'
import { actions as audioActions } from 'modules/audio/actions'
import { actions as browseActions } from 'modules/browse/actions'

// Components
import { Error, Layout } from '@tracklib/kit'
import { ContentHeader } from 'kit'
import InfoItem2 from 'components/base/infoitem2'
import SEO from 'components/base/seo'
import TrackList from 'components/browse/tracklist'
import Filter from 'components/filter'

// Hooks
import { useArtists, useTracks, useSongs, useGenres, useRegions } from 'modules/entities/hooks'
import { useSearchQuery } from 'modules/browse/hooks'
import { useRequest } from 'modules/requests/hooks'

//  Utils
import { isBrowser } from '@process'
import { createSearchQuery } from 'pages/collection'

// Types
import { Dispatch, Genre, Region } from 'types'

export const loadInitialData =
  ({
    location: {
      params: { slug },
    },
  }: {
    location: { params: { slug: string } }
  }) =>
  async (dispatch: Dispatch) => {
    const searchQuery = createSearchQuery({ artist: slug }, '')
    return Promise.all([dispatch(artistsActions.fetchArtistInfo(slug)), dispatch(browseActions.fetchTracksBySearchQuery(searchQuery))])
  }

const ArtistPage = () => {
  const [genres, setGenres] = useState<Array<Genre>>([])
  const [regions, setRegions] = useState<Array<Region>>([])
  const dispatch = useDispatch()
  const { slug: artistSlug } = useParams() as { slug: string }
  const slug = artistSlug || ''

  const queryObj = { artist: slug }
  const songListProps = useSearchQuery(queryObj, slug)
  const fetchArtistInfoRequest = useRequest('FETCH_ARTIST_INFO', slug)
  const playAllRequest = useRequest('PREPARE_PLAY_SEARCH_QUERY')
  const artists = useArtists()
  const artistList = Object.keys(artists || {}).map(id => artists[id])
  const artist = artistList.find(a => a.slug === slug)
  useEffect(() => {
    if (!artist?.image && !fetchArtistInfoRequest.isLoading && !fetchArtistInfoRequest.hasError) {
      dispatch(artistsActions.fetchArtistInfo(slug))
    }
  }, [dispatch, artist, fetchArtistInfoRequest, slug])

  const tracks = useTracks()
  const songs = useSongs()
  const songList = songListProps.trackIds.map(({ id: trackId }) => {
    const songId = tracks[String(trackId)].song
    return songs[String(songId)]
  })
  const genreIds = Array.from(new Set(songList.reduce<Array<number>>((agg, song) => [...agg, ...song.genres], [])))
  const regionIds = Array.from(new Set(songList.reduce<Array<number>>((agg, song) => [...agg, ...song.regions], [])))
  const allGenres = useGenres()
  const allRegions = useRegions()
  const genreList = genreIds.map(genreId => allGenres[String(genreId)])
  const regionList = regionIds.map(regionId => allRegions[String(regionId)])

  /* Prevent genres/regions from changing when filtering */
  useEffect(() => {
    if (genreIds.length > genres.length && genres !== genreList) {
      setGenres(genreList)
    }
  }, [genreIds.length]) // eslint-disable-line
  useEffect(() => {
    if (regionIds.length > regions.length && genres !== regionList) {
      setRegions(regionList)
    }
  }, [regionIds.length]) // eslint-disable-line

  if (!artist || !slug) {
    if (fetchArtistInfoRequest.isDone && isBrowser) {
      return <Error message="Artist couldn't be found" />
    }
  }

  return (
    <Layout.FullWidth>
      {artist ? (
        <SEO
          title={`${artist.name} on Tracklib | Sample Instantly`}
          description={`${artist.name}, an artist on Tracklib`}
          image={artist.image?.uri}
          type="music:musician"
        />
      ) : (
        <SEO title="Artist | Tracklib" />
      )}
      {artist ? (
        <ContentHeader
          playAll={() => dispatch(audioActions.playSearchQuery({ artist: slug }))}
          playAllRequest={playAllRequest}
          image={artist.image?.uri}
          type="Artist"
          title={artist.name}
          description={artist.bio}
          infoItems={[
            <InfoItem2 genres={genres} showAll displayAsRow />,
            ...(regions.length > 0 ? [<InfoItem2 regions={regions} showAll displayAsRow />] : []),
          ]}
          sharePath={`/artist/${slug}`}
          adminEditPath={`/tracks/artist/${artist.id}/change`}
          breadcrumbs={[
            {
              text: 'Artists',
              link: '/artists/',
            },
            {
              text: artist.name,
              link: `/artist/${slug}`,
            },
          ]}
        />
      ) : (
        <ContentHeader loading />
      )}
      <Filter queryObj={queryObj} />
      <TrackList {...songListProps} />
    </Layout.FullWidth>
  )
}

export default ArtistPage
